import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import Button from '../Button/Button';
import IconCollection from '../IconCollection/IconCollection';

import { deleteCard, onCreateTokenForSavedCard, onFetchCustomerCards } from '../../util/api';
import { FormattedMessage } from '../../util/reactIntl';
import { fetchIPAddress } from '../../util/urlHelpers';
import { updateProfile } from '../../containers/ProfileSettingsPage/ProfileSettingsPage.duck';

import css from './TapCardElement.module.css';

const TapCardElement = (props) => {
  const {
    intl,
    currentUser,
    handleSubmitForm,
    payinTotal,
    selectedAddress = true,
    progress,
    shouldDisable,
  } = props;
  const dispatch = useDispatch();
  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState(null);
  const [savedCardToken, setSavedCardToken] = useState(null);
  const [inProgress, setInProgress] = useState(false);
  const [isInvalid, setIsInvalid] = useState(true);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isCardLoading, setIsCardLoading] = useState(true);

  const { amount, currency } = payinTotal || {};

  const { email, profile } = currentUser?.attributes || {};
  const { firstName, lastName, displayName, protectedData, publicData } = profile || {};
  const { customerId } = publicData || {};
  const { cards: savedCards, mobile_phone_number } = protectedData || {};
  const { callingCode, phoneNumber } = mobile_phone_number || {};

  const cardContainerRef = useRef(null);
  const [sdkLoaded, setSdkLoaded] = useState(!!window.CardSDK);

  useEffect(() => {
    if (window.CardSDK) {
      setSdkLoaded(true);
      return;
    }

    const interval = setInterval(() => {
      if (window.CardSDK) {
        setSdkLoaded(true);
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!sdkLoaded) return;

    const {
      renderTapCard,
      Theme,
      Currencies,
      Direction,
      Edges,
      Locale,
      // tokenize is used in the button click handler, so we'll access it directly from window.CardSDK there
    } = window.CardSDK || {};

    if (!renderTapCard) {
      console.error('Tap SDK renderTapCard function is not available');
      return;
    }

    // Check if Arabic language is selected
    const isArabic = typeof window !== 'undefined' && localStorage.getItem("language") === "Arabic";

    const { unmount } = renderTapCard(cardContainerRef.current.id, {
      publicKey: process.env.REACT_APP_TAP_PUBLIC_KEY,
      merchant: {
        id: process.env.REACT_APP_TAP_MERCHANT_ID,
      },
      transaction: {
        amount: amount / 100, // replace with actual amount
        currency: (Currencies && Currencies[currency]) || currency || (Currencies && Currencies.SAR) || 'SAR',
      },
      customer: {
        id: customerId, // Replace with customer id
        name: [
          {
            lang: (Locale && Locale.AR) ? (isArabic ? Locale.AR : Locale.EN) : (isArabic ? 'ar' : 'en'),
            first: firstName,
            last: lastName,
            middle: '',
          },
        ],
        nameOnCard: displayName,
        editable: true,
        contact: {
          email: email,
          phone: {
            countryCode: callingCode, // replace with original country code
            number: phoneNumber, // replace with original number
          },
        },
      },
      acceptance: {
        supportedBrands: ['AMERICAN_EXPRESS', 'VISA', 'MASTERCARD', 'MADA'],
        supportedCards: 'ALL',
      },
      fields: {
        cardHolder: true,
      },
      addons: {
        displayPaymentBrands: true,
        loader: true,
        // saveCard: true,
      },
      interface: {
        locale: (Locale && Locale.AR) ? (isArabic ? Locale.AR : Locale.EN) : (isArabic ? 'ar' : 'en'),
        theme: (Theme && Theme.LIGHT) || 'light',
        edges: (Edges && Edges.CURVED) || 'curved',
        direction: (Direction && Direction.RTL) ? (isArabic ? Direction.RTL : Direction.LTR) : (isArabic ? 'rtl' : 'ltr'),
      },
      onError: data => {
        console.log('onError', data);
        setInProgress(false);
      },
      onSuccess: data => {
        setIsProcessingPayment(true);
        handleSubmitForm({ data, formValues: { saveAfterOnetimePayment: ["saveAfterOnetimePayment"] } });
      },
      onInvalidInput: data => {
        console.log('onInvalidInput', data);
        setIsInvalid(data);
        if (data) {
          setInProgress(false);
        }
      },
      onReady: () => {
        console.log('onReady');
        setIsCardLoading(false);
      },
      // onFocus: () => console.log('onFocus'),
      // onBinIdentification: data => console.log('onBinIdentification', data),
      // onChangeSaveCardLater: isSaveCardSelected => formApi.change('saveCardLater', isSaveCardSelected),
    });

    customerId && onFetchCustomerCards({ customerId })
      .then(res => {
        const cards = res?.data || [];
        // Filter out duplicate cards based on their id
        const uniqueCards = cards.filter((card, index, self) =>
          index === self.findIndex((c) => c.id === card.id)
        );
        setCards(uniqueCards);
      })
      .catch(error => {
        console.error("Could not fetch cards", error);
      });

    return () => {
      if (unmount) {
        unmount();
      }
    };
  }, [sdkLoaded]);

  useEffect(() => {
    if (!progress && !shouldDisable) {
      setIsProcessingPayment(false);
      setInProgress(false);
      setSelectedCard(null);
      setSavedCardToken(null);
    }
  }, [progress, shouldDisable]);

  const handleDeleteCard = async (cardId) => {
    try {
      // Call the API to delete the card
      await deleteCard({ customer_id: customerId, card_id: cardId });

      // Remove the card from the state to update the UI
      setCards(prevCards => prevCards.filter(card => card.id !== cardId));
      // Also clear selection if the deleted card was selected
      setSelectedCard(prev => (prev?.id === cardId ? null : prev));

      // Sync profile: remove the deleted card entry from protectedData.cards
      const oldCards = currentUser?.attributes?.profile?.protectedData?.cards || [];
      const updatedCards = oldCards.filter(c => c.cardId !== cardId);
      try {
        await dispatch(updateProfile({ protectedData: { cards: updatedCards } }));
      } catch (e) {
        console.error('Failed to update profile after card deletion', e);
      }
      alert(intl.formatMessage({ id: "TapCardElement.deleteCardSuccess" }));
    } catch (error) {
      console.error(intl.formatMessage({ id: "TapCardElement.deleteCardError" }), error);
      alert(intl.formatMessage({ id: "TapCardElement.deleteCardFail" }));
    }
  };

  const submitDisabled = !selectedAddress || (selectedCard && !savedCardToken) || progress || inProgress || (!selectedCard && isInvalid) || shouldDisable;

  const arabicLanguage = typeof window !== 'undefined' && localStorage.getItem("language") === "Arabic";

  return (
    <div className={classNames(css.cardWrapper, arabicLanguage && css.arabicDirection)}>
      {isProcessingPayment && (
        <div className={css.loadingOverlay}>
          <div className={css.loadingContent}>
            <div className={css.spinner}></div>
            <p className={css.loadingText}>
              <FormattedMessage id="TapCardElement.processingPayment" defaultMessage="Processing payment..." />
            </p>
          </div>
        </div>
      )}

      {isCardLoading && !selectedCard && (
        <div className={css.loadingOverlay}>
          <div className={css.loadingContent}>
            <div className={css.spinner}></div>
            <p className={css.loadingText}>
              <FormattedMessage id="TapCardElement.loadingCard" defaultMessage="Loading card..." />
            </p>
          </div>
        </div>
      )}

      {cards?.length ? (
        <div className={css.cards}>
          {cards.map(card => {
            const { brand, name, last_four, exp_month, exp_year, id } = card || {};

            const isSelected = selectedCard?.id === card?.id;
            return (
              <React.Fragment key={card.id}>

                <div>
                  <div
                    onClick={async () => {
                      if (isSelected) {
                        setSelectedCard(null);
                        setSavedCardToken(null);
                        return;
                      }
                      setInProgress(true);
                      setSelectedCard(card);

                      const client_ip = await fetchIPAddress() || "127.0.0.1";
                      const effectiveCustomerId = card?.customer_id || customerId;

                      onCreateTokenForSavedCard
                        ({
                          params: {
                            client_ip: client_ip,
                            saved_card: {
                              card_id: card?.id,
                              customer_id: effectiveCustomerId,
                            }
                          }
                        })
                        .then(res => {
                          setSavedCardToken(res);
                          setInProgress(false);
                        })
                        .catch(err => {
                          console.error(err);
                          setSavedCardToken(null);
                          setSelectedCard(null);
                          setInProgress(false);
                        })
                    }}
                    className={classNames(css.card, isSelected && css.selectedCard)}>
                    <div className={css.topContent}>
                      <div className={css.cardHeader}>
                        <span className={css.cardName}>{name}</span>
                        <span className={css.brandText}>{brand}</span>
                      </div>
                      <div className={css.cardFooter}>
                        <span>**** {last_four} <span>{exp_month}/{exp_year}</span></span>
                      </div>
                      <div className={css.deleteCardIcon} onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCard(id);
                      }}>
                        <span className={css.crossIcon}><IconCollection name="close_card" /></span>
                      </div>
                    </div>

                  </div>

                </div>
              </React.Fragment>
            )
          })}
        </div>
      ) : null}

      <div className={classNames(css.tapCardWrapper, selectedCard && css.hiddenElement)}>
        {isCardLoading && (
          <div className={css.shimmerLoader}>
            <div className={css.shimmerBlock}></div>
            <div className={css.shimmerRow}>
              <div className={css.shimmerBlock}></div>
              <div className={css.shimmerBlock}></div>
            </div>
            <div className={css.shimmerBlock}></div>
          </div>
        )}
        <div
          className={classNames(css.cardField, isCardLoading && css.hiddenElement)}
          ref={cardContainerRef}
          id="card-sdk-id"
        ></div>
      </div>

      <Button
        type="button"
        disabled={submitDisabled}
        inProgress={progress || inProgress}
        onClick={async e => {
          e.preventDefault();

          if (selectedCard && savedCardToken) {
            // setInProgress(true);
            const cardDetails = savedCards?.find(card => card?.cardId === selectedCard?.id);
            const { paymentAgreementId } = cardDetails || {};

            if (!paymentAgreementId) {
              alert(intl.formatMessage({ id: "TapCardElement.PaymentAgreementIdError" }));
              setInProgress(false);
              return;
            }

            setIsProcessingPayment(true);
            await handleSubmitForm({
              data: savedCardToken,
              formValues: {},
              useSavedCard: true,
              paymentAgreementId,
            });
            console.log('[TapCardElement] ✅ Saved card payment submitted');
            setInProgress(false);
            setSelectedCard(null);
            setSavedCardToken(null);
          }
          else {
            setInProgress(true);
            if (window.CardSDK && window.CardSDK.tokenize) {
              window.CardSDK.tokenize();
            } else {
              console.error('Tap SDK tokenize method is not available');
              setInProgress(false);
            }
          }
        }}
        className={css.tokeniseCard}
      >
        <FormattedMessage id="TapCardElement.submitButtonText" />
      </Button>
    </div>
  )
};

export default TapCardElement;
